import React, { memo } from 'react';
import { formatCurrency } from '../../utilities/general-utils';
import './RefinanceAccountItem.css';

/**
 * Individual account item for refinance selection
 * Displays account info with checkbox for selection
 */
const RefinanceAccountItem = memo(({ 
    account, 
    isSelected, 
    onSelect, 
    onDelete = null, 
    isEven = false 
}) => {
    const handleSelectionChange = (e) => {
        // Prevent event if clicking on delete button
        if (e && e.target && e.target.closest('button')) {
            return;
        }
        onSelect(account.id, !isSelected);
    };

    const handleDelete = () => {
        if (onDelete && window.confirm(`Are you sure you want to remove "${account.name}"?`)) {
            onDelete(account.id);
        }
    };

    const cardClasses = [
        'refinance-account-item',
        isSelected && 'refinance-account-item--selected'
    ].filter(Boolean).join(' ');

    return (
        <div className={cardClasses} onClick={handleSelectionChange}>
            {/* Selection Checkbox */}
            <input
                type="checkbox"
                checked={isSelected}
                onChange={() => {}} // Handled by card click
                className="refinance-account-item__checkbox"
            />

            {/* Delete Button for Manual Accounts */}
            {onDelete && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDelete();
                    }}
                    className="refinance-account-item__delete-button"
                    title="Remove manual account"
                >
                    ✕
                </button>
            )}

            {/* Account Header */}
            <div className="refinance-account-item__header">
                <div className="refinance-account-item__name">
                    {account.name}
                </div>
                <div className="refinance-account-item__institution">
                    <span>{account.institution}</span>
                    {account.isManual && (
                        <span className="refinance-account-item__manual-badge">
                            Manual
                        </span>
                    )}
                </div>
            </div>

            {/* Account Details Grid */}
            <div className="refinance-account-item__details">
                {/* Balance */}
                <div className="refinance-account-item__detail">
                    <div className="refinance-account-item__detail-label">
                        Balance
                    </div>
                    <div className="refinance-account-item__detail-value refinance-account-item__detail-value--balance">
                        {formatCurrency(account.balance)}
                    </div>
                </div>

                {/* APR */}
                <div className="refinance-account-item__detail">
                    <div className="refinance-account-item__detail-label">
                        APR
                    </div>
                    <div className="refinance-account-item__detail-value">
                        {account.apr ? `${(parseFloat(account.apr) * 100).toFixed(2)}%` : 'N/A'}
                    </div>
                </div>

                {/* Monthly Payment */}
                <div className="refinance-account-item__detail">
                    <div className="refinance-account-item__detail-label">
                        Monthly Payment
                    </div>
                    <div className="refinance-account-item__detail-value">
                        {formatCurrency(account.monthly_payment || account.defaultPaymentAmount || account.minimum_payment || 0)}
                    </div>
                    {account.paymentType === 'fixed' && account.fixed_payment && (
                        <div className="refinance-account-item__detail-note">
                            Fixed: {formatCurrency(account.fixed_payment)}
                        </div>
                    )}
                </div>

                {/* Account Type */}
                <div className="refinance-account-item__detail">
                    <div className="refinance-account-item__detail-label">
                        Type
                    </div>
                    <div className="refinance-account-item__type-badge">
                        {account.type}
                    </div>
                </div>
            </div>

            {/* Selection Indicator */}
            {isSelected && (
                <div className="refinance-account-item__selection-indicator" />
            )}
        </div>
    );
});

RefinanceAccountItem.displayName = 'RefinanceAccountItem';

export default RefinanceAccountItem;